import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useResourceMutations } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { useAuth } from "@/context/AuthContext";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, TextareaField, SelectField, Field } from "@/components/kit/fields";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { TIMEZONES_WITH_AUTO } from "@/lib/timezones";
import { FileUpload } from "@/components/kit/FileUpload";
import { BusinessPermissions } from "@/components/settings/BusinessPermissions";
import { OfferRatesTab } from "@/components/users/OfferRatesTab";
import { TwoFactorAdminControl } from "@/components/settings/AccountSecurity";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

// Super Admin is a system role and is intentionally NOT selectable here.
// Tenant Admins manage only their own roles (Manager/Buyer) — they cannot create other Tenant Admins.
const ROLE_OPTS_ALL = [
  { value: "tenant_admin", label: "Tenant Admin" },
  { value: "manager", label: "Manager" },
  { value: "buyer", label: "Buyer" },
];
const ROLE_LABEL = { tenant_admin: "Tenant Admin", manager: "Manager", buyer: "Buyer" };
const LANGS = ["English", "Russian", "Spanish", "German"];

const empty = {
  avatar: "", name: "", username: "", email: "", password: "", role: "buyer", manager_id: "",
  status: "active", telegram: "", discord: "",
  timezone: "", language: "English", notes: "",
  permissions_granted: [], permissions_revoked: [], allow_subid_postback: false, allow_source_postback: false,
};

function SectionTitle({ children }) {
  return <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">{children}</p>;
}

export function UserDrawer({ open, item, onOpenChange }) {
  const opts = useOptions();
  const { user: me } = useAuth();
  const { create, update } = useResourceMutations("users");
  const [form, setForm] = useState(empty);
  const baseline = useRef(JSON.stringify(empty));
  const [tab, setTab] = useState("Profile");
  const isEdit = !!item?.id;
  const isBuyer = form.role === "buyer";

  // A Manager can only create Buyers; a Tenant Admin (and Super Admin) can create any tenant role.
  const roleOpts = me?.role === "manager"
    ? ROLE_OPTS_ALL.filter((r) => r.value === "buyer")
    : ROLE_OPTS_ALL;
  // Keep the existing role visible when editing a record whose role isn't normally creatable.
  const roleOptsFinal = (isEdit && form.role && !roleOpts.some((r) => r.value === form.role))
    ? [...ROLE_OPTS_ALL.filter((r) => r.value === form.role), ...roleOpts]
    : roleOpts;

  // Responsible users = tenant admins + managers (self can own buyers too).
  const responsibles = (opts.responsibles && opts.responsibles.length
    ? opts.responsibles
    : opts.managers.map((m) => ({ ...m, role: "manager" })));
  const responsibleOpts = responsibles.map((r) => ({
    value: r.id,
    label: `${r.name}${r.id === me?.id ? " (you)" : ""}${r.role === "tenant_admin" ? " · Admin" : ""}`,
  }));

  useEffect(() => {
    if (open) {
      setTab("Profile");
      let init;
      if (item) {
        init = { ...empty, ...item, password: "",
          permissions_granted: item.permissions_granted || [], permissions_revoked: item.permissions_revoked || [] };
      } else {
        // creating: if the creator is a manager, default-assign new buyers to themselves
        init = { ...empty, manager_id: me?.role === "manager" ? me.id : "" };
      }
      setForm(init);
      baseline.current = JSON.stringify(init);
    }
  }, [open, item, me]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== baseline.current;

  // Role is a template: switching role resets per-user overrides.
  const changeRole = (role) =>
    setForm((f) => ({ ...f, role, permissions_granted: [], permissions_revoked: [],
      manager_id: role === "buyer" ? f.manager_id : "" }));

  const save = async () => {
    if (!form.name || !form.email) { setTab("Profile"); return toast.error("Name and email are required"); }
    if (!isEdit && !form.password) { setTab("Profile"); return toast.error("Password is required"); }
    // Manager is required for new buyers; legacy buyers saved without one can still be edited.
    const legacyUnassigned = isEdit && item?.role === "buyer" && !item?.manager_id;
    if (isBuyer && !form.manager_id && !legacyUnassigned) { setTab("Profile"); return toast.error("Please assign a responsible manager for this buyer"); }
    const body = { ...form };
    delete body.balance;
    if (!isBuyer) body.manager_id = "";
    if (isEdit && !body.password) delete body.password;
    if (isEdit) await update.mutateAsync({ id: item.id, body });
    else await create.mutateAsync(body);
    onOpenChange(false);
  };

  const Tab = ({ name }) => (
    <button onClick={() => setTab(name)} data-testid={`user-tab-${name.toLowerCase()}`}
      className={cn("relative px-3 py-2 text-sm transition-colors", tab === name ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
      {name}{tab === name && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-white" />}
    </button>
  );

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title={isEdit ? "Edit User" : "New User"}
      subtitle={isEdit ? form.email : "Add a member to your network"} size="lg" dirty={dirty}
      onSave={save} saving={create.isPending || update.isPending} saveLabel={isEdit ? "Save changes" : "Create user"} testId="user-drawer">

      <div className="-mt-1 mb-5 flex gap-1 border-b border-white/[0.08]">
        <Tab name="Profile" /><Tab name="Permissions" />
        {isEdit && me?.role !== "buyer" && <Tab name="Offer Rates" />}
      </div>

      {tab === "Profile" && (
        <div className="space-y-6">
          {/* Identity card */}
          <div className="flex items-center gap-5 rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-4">
            <FileUpload value={form.avatar} onChange={(v) => set("avatar", v)} kind="avatar" aspect="favicon" testId="user-avatar" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-base font-medium text-foreground">{form.name || "New user"}</div>
              <div className="truncate text-sm text-muted-foreground">{form.email || "no email yet"}</div>
              <span className="mt-1.5 inline-block rounded-full border border-white/[0.1] bg-white/[0.04] px-2 py-0.5 text-xs text-foreground/80">{ROLE_LABEL[form.role]}</span>
            </div>
          </div>

          {/* Account */}
          <div className="space-y-3">
            <SectionTitle>Account</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <TextField label="Full name" value={form.name} onChange={(v) => set("name", v)} testId="user-name" />
              <TextField label="Username" value={form.username} onChange={(v) => set("username", v)} testId="user-username" autoComplete="off" name="user-username-field" />
              <TextField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="user-email" autoComplete="off" name="user-email-field" />
              <TextField label={isEdit ? "New password (blank = keep)" : "Password"} type="password" value={form.password} onChange={(v) => set("password", v)} testId="user-password" autoComplete="new-password" name="user-new-password-field" />
            </div>
          </div>

          {/* Role & assignment */}
          <div className="space-y-3">
            <SectionTitle>Role & assignment</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="Role" value={form.role} onChange={changeRole} options={roleOptsFinal} testId="user-role" />
              <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={["active", "hold", "disabled"]} testId="user-status" />
              {isBuyer && (
                <SelectField label="Assigned manager *" value={form.manager_id} onChange={(v) => set("manager_id", v)}
                  options={responsibleOpts} placeholder="Choose who manages this buyer" testId="user-manager" />
              )}
            </div>
            {isBuyer && <p className="text-xs text-muted-foreground">The responsible user can be you (Tenant Admin) or any manager — they own this buyer.</p>}
          </div>

          {/* Contacts */}
          <div className="space-y-3">
            <SectionTitle>Contacts</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <TextField label="Telegram" value={form.telegram} onChange={(v) => set("telegram", v)} testId="user-telegram" placeholder="@handle" />
              <TextField label="Discord" value={form.discord} onChange={(v) => set("discord", v)} testId="user-discord" />
            </div>
          </div>

          {/* Locale */}
          <div className="space-y-3">
            <SectionTitle>Locale</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Timezone">
                <SearchSelect value={form.timezone} onChange={(v) => set("timezone", v)} options={TIMEZONES_WITH_AUTO}
                  placeholder="Auto — device timezone" searchPlaceholder="Search timezone…" testId="user-timezone" widthClass="w-[320px]" triggerClass="h-10 w-full" />
              </Field>
              <SelectField label="Language" value={form.language} onChange={(v) => set("language", v)} options={LANGS} testId="user-language" />
            </div>
          </div>

          <TextareaField label="Notes" value={form.notes} onChange={(v) => set("notes", v)} testId="user-notes" />

          {isEdit && me?.role !== "buyer" && item?.id && (
            <TwoFactorAdminControl userId={item.id} userName={form.name || form.email} />
          )}
        </div>
      )}

      {tab === "Permissions" && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{ROLE_LABEL[form.role]}</span> comes with a standard set of permissions.
            Toggle anything below to tailor access for this specific person.
          </p>
          {isBuyer && (
            <div className="space-y-3">
              <SectionTitle>Outgoing postback macros</SectionTitle>
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-4" data-testid="user-allow-subid-row">
                <Checkbox checked={!!form.allow_subid_postback} onCheckedChange={(v) => set("allow_subid_postback", !!v)} data-testid="user-allow-subid" className="mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Allow {"{sub_id}"} in outgoing postbacks</div>
                  <p className="mt-0.5 text-xs text-muted-foreground">When off, this buyer cannot save postbacks that use the advertiser {"{sub_id}"} macro. Other macros ({"{sub_id_1}"}…{"{sub_id_9}"}) are unaffected.</p>
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-4" data-testid="user-allow-source-row">
                <Checkbox checked={!!form.allow_source_postback} onCheckedChange={(v) => set("allow_source_postback", !!v)} data-testid="user-allow-source" className="mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">Allow {"{source}"} in outgoing postbacks</div>
                  <p className="mt-0.5 text-xs text-muted-foreground">When off, this buyer cannot save postbacks that use the {"{source}"} (partner ID) macro. Tenant admins and managers are always allowed.</p>
                </div>
              </label>
            </div>
          )}
          <BusinessPermissions role={form.role} granted={form.permissions_granted} revoked={form.permissions_revoked}
            onChange={(g, r) => setForm((f) => ({ ...f, permissions_granted: g, permissions_revoked: r }))} />
        </div>
      )}

      {tab === "Offer Rates" && isEdit && (
        <OfferRatesTab userId={item.id} userName={form.name || form.email} isStaff={me?.role !== "buyer"} />
      )}
    </FormDrawer>
  );
}
